import { Icon } from '@design/components/icon/Icon';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { Button, ButtonProps } from '../button/Button';
import styles from './Alert.module.scss';

export interface AlertProps {
  /**
   * Severity level. Drives both the color treatment and the auto icon
   * shown in the icon column. Defaults to `AlertType.Error`.
   */
  type?: AlertType;
  /**
   * Layout: `'inline'` sits in the document flow, `'toast'` is a
   * compact notification typically rendered into a portal.
   * Defaults to `'toast'`.
   */
  variant?: AlertVariant;
  /** Bold heading line above the message. */
  title?: ReactNode;
  /** Body content of the alert. Accepts ReactNode for rich content. */
  message?: ReactNode;
  /** Show the auto icon for the current `type`. Defaults to `true`. */
  withIcon?: boolean;
  /** When provided, renders a close (×) button that calls this handler. */
  onClose?: () => void;
  /** Extra class forwarded to the root element. */
  className?: string;
  /** Primary action button. Spreads as `ButtonProps` plus `onClick`. Rendered with `size="small"`. */
  primaryBtn?: ButtonConfig;
  /** Secondary action button. Spreads as `ButtonProps` plus `onClick`. Rendered as `size="small" variant="secondary"`. */
  secondaryBtn?: ButtonConfig;
  /** Replaces the auto per-`type` icon. Use when the default icon doesn't fit your message. */
  customIcon?: ReactNode;
}

interface ButtonConfig extends ButtonProps {
  onClick: () => void;
}

export type AlertVariant = 'inline' | 'toast';

export enum AlertType {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
}

const typeClassMap: Record<AlertType, string> = {
  success: styles.typeSuccess,
  warning: styles.typeWarning,
  error: styles.typeError,
  info: styles.typeInfo,
};

const variantClassMap: Record<AlertVariant, string> = {
  inline: styles.variantInline,
  toast: styles.variantToast,
};

const icons = {
  success: (
    <Icon
      fillColor="var(--icon-success)"
      iconName="CheckmarkCircle"
      size={20}
    />
  ),
  warning: (
    <Icon
      fillColor="var(--icon-warning)"
      iconName="ExclamationCircle"
      size={20}
    />
  ),
  error: (
    <Icon fillColor="var(--icon-critical)" iconName="CloseCircle" size={20} />
  ),
  info: (
    <Icon
      fillColor="var(--icon-primary)"
      iconName="InformationCircle"
      size={20}
    />
  ),
};

/**
 * Inline or toast notification surfacing a status message
 * (success, warning, error, or info).
 *
 * Renders an automatic icon for the chosen `type` (override with
 * `customIcon`, or hide via `withIcon={false}`), an optional close
 * button, and up to two action buttons.
 */
export const Alert = ({
  type = AlertType.Error,
  variant = 'toast',
  withIcon = true,
  title,
  message,
  onClose,
  className,
  primaryBtn,
  secondaryBtn,
  customIcon,
  ...rest
}: AlertProps) => {
  return (
    <div
      className={clsx(
        styles.alert,
        typeClassMap[type],
        variantClassMap[variant],
        className
      )}
      data-testid="ds-alert"
      data-alert-root
      {...rest}
    >
      {withIcon && (
        <div className={styles.icon} data-testid="icon">
          {customIcon || icons[type]}
        </div>
      )}
      <div className={styles.content} data-alert-content>
        <div className={styles.contentSection}>
          <div className={styles.textWrapper}>
            {title && <p className={styles.title}>{title}</p>}
            {message && (
              <div className={styles.message} data-testid="message">
                {message}
              </div>
            )}
          </div>
          {onClose && (
            <div>
              <button
                className={styles.close}
                data-testid="alert-close-button"
                onClick={onClose}
              >
                <Icon iconName="Close" size={13} />
              </button>
            </div>
          )}
        </div>

        {(primaryBtn || secondaryBtn) && (
          <div className={styles.buttonsContainer} data-alert-buttons>
            {primaryBtn && <Button {...primaryBtn} size="small" />}
            {secondaryBtn && (
              <Button {...secondaryBtn} size="small" variant="secondary" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
